import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import { AnimalTag } from '../../features/common/AnimalTag';
import {
  breedingKpisApi, breedingRecordsApi, calvingRecordsApi, heatDetectionsApi,
  lambingKiddingRecordsApi, pregnancyRecordsApi, semenInventoryApi, semenMasterApi,
} from '../../services/resources';
import { getApiErrorMessage } from '../../services/api';
import { formatDate, formatNumber, today } from '../../utils/format';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSCardSkeleton } from '../../components/ILMSLoadingState';
import type {
  BreedingAlert, BreedingKPI, BreedingRecord, CalvingRecord, HeatDetection, LambingKiddingRecord,
  PregnancyRecord, SemenInventory, SemenMaster,
} from '../../types';
import type { ModuleConfig } from '../../features/common/types';

// Page-vs-tab call: v2 split v1's single Breeding_Records into six tables
// (Heat_Detections / Breeding_Records / Pregnancy_Records / Calving_Records /
// Lambing_Kidding_Records / Breeding_KPIs), matching WF-03's step-by-step
// workflow — all six live as tabs on one "Breeding" page rather than six nav
// items. Semen_Master/Semen_Inventory (the AI catalogue) are included here
// since they only exist to support the Service tab.

const heatConfig: ModuleConfig<HeatDetection> = {
  title: 'Heat Detections', entityLabel: 'heat detection', module: 'Breeding', queryKey: 'heat-detections', api: heatDetectionsApi, idKey: 'id',
  referenceKeys: ['animals'], defaultValues: { heat_date: today() },
  columns: [
    { key: 'animal', header: 'Animal', primary: true, render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'date', header: 'Heat Date', render: (r) => formatDate(r.heat_date) },
    { key: 'intensity', header: 'Intensity', render: (r) => r.intensity || '—' },
    { key: 'action', header: 'Action Taken', render: (r) => r.action_taken || '—' },
  ],
  fields: [
    { name: 'animal_id', label: 'Animal (Female)', type: 'select', section: 'Heat', required: true, optionsKey: 'animals' },
    { name: 'heat_date', label: 'Heat Date', type: 'date', section: 'Heat', required: true },
    { name: 'signs_observed', label: 'Signs Observed', type: 'textarea', section: 'Heat', optional: true, fullWidth: true },
    { name: 'intensity', label: 'Intensity', type: 'select', section: 'Heat', optional: true, staticOptions: [
      { value: 'Weak', label: 'Weak' }, { value: 'Moderate', label: 'Moderate' }, { value: 'Strong', label: 'Strong' },
    ] },
    { name: 'action_taken', label: 'Action Taken', type: 'select', section: 'Follow-up', optional: true, staticOptions: [
      { value: 'AIScheduled', label: 'AI Scheduled' }, { value: 'NaturalService', label: 'Natural Service' },
      { value: 'Observed', label: 'Observed Only' }, { value: 'Ignored', label: 'Ignored' },
    ] },
  ],
};

const serviceConfig: ModuleConfig<BreedingRecord> = {
  title: 'Service (AI / Natural)', entityLabel: 'service record', module: 'Breeding', queryKey: 'breeding-records', api: breedingRecordsApi, idKey: 'id',
  referenceKeys: ['animals', 'suppliers'], addLabel: 'Record Service',
  defaultValues: { breeding_type: 'AI', outcome: 'Pending' },
  columns: [
    { key: 'animal', header: 'Animal', primary: true, render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'type', header: 'Type', render: (r) => r.breeding_type },
    { key: 'date', header: 'AI / Service Date', render: (r) => formatDate(r.ai_date) },
    { key: 'bull', header: 'Bull / Semen ID', render: (r) => [r.bull_name, r.bull_id].filter(Boolean).join(' · ') || '—', hideOnTablet: true },
    // Computed from the species' breeding days (Farm Setup → Animal Types).
    { key: 'repeat', header: 'Repeat Heat Check', render: (r) => formatDate(r.repeat_heat_check_date), hideOnTablet: true },
    { key: 'pd_due', header: 'PD Due', render: (r) => formatDate(r.pd_due_date) },
    { key: 'pd_result', header: 'PD Result', render: (r) => (r.pd_result ? <ILMSStatusBadge status={r.pd_result} size="sm" /> : '—') },
    { key: 'expected', header: 'Expected Calving', render: (r) => formatDate(r.expected_calving_date) },
    { key: 'dry_off', header: 'Dry-off Date', render: (r) => formatDate(r.dry_off_date), hideOnTablet: true },
    { key: 'stage', header: 'Stage', render: (r) => r.stage || '—' },
    { key: 'outcome', header: 'Outcome', render: (r) => (r.outcome ? <ILMSStatusBadge status={r.outcome} size="sm" /> : '—'), hideOnTablet: true },
  ],
  fields: [
    { name: 'animal_id', label: 'Animal (Female)', type: 'select', section: 'Service', required: true, optionsKey: 'animals' },
    { name: 'breeding_type', label: 'Type', type: 'select', section: 'Service', required: true, staticOptions: [{ value: 'Natural', label: 'Natural' }, { value: 'AI', label: 'AI' }] },
    { name: 'ai_date', label: 'AI Date', type: 'date', section: 'Service', optional: true },
    { name: 'technician', label: 'Technician', type: 'text', section: 'Service', optional: true },
    { name: 'bull_name', label: 'Bull Name', type: 'text', section: 'Sire', optional: true },
    { name: 'bull_id', label: 'Bull ID', type: 'text', section: 'Sire', optional: true },
    { name: 'straw_number', label: 'Straw Number', type: 'text', section: 'Sire', optional: true },
    { name: 'supplier_id', label: 'Semen Supplier', type: 'select', section: 'Sire', optional: true, optionsKey: 'suppliers' },
    { name: 'outcome', label: 'Outcome', type: 'select', section: 'Outcome', optional: true, staticOptions: [
      { value: 'Pending', label: 'Pending' }, { value: 'Conceived', label: 'Conceived' }, { value: 'NotConceived', label: 'Not Conceived' }, { value: 'Aborted', label: 'Aborted' },
    ] },
  ],
};

const pregnancyConfig: ModuleConfig<PregnancyRecord> = {
  title: 'Pregnancy Checks', entityLabel: 'pregnancy check', module: 'Pregnancy', queryKey: 'pregnancy-records', api: pregnancyRecordsApi, idKey: 'id',
  referenceKeys: ['animals'], defaultValues: { test_date: today(), result: 'Positive' },
  columns: [
    { key: 'animal', header: 'Animal', primary: true, render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'date', header: 'Test Date', render: (r) => formatDate(r.test_date) },
    { key: 'result', header: 'Result', render: (r) => <ILMSStatusBadge status={r.result} size="sm" /> },
    { key: 'expected', header: 'Expected Birth', render: (r) => formatDate(r.expected_birth_date) },
  ],
  fields: [
    { name: 'animal_id', label: 'Animal', type: 'select', section: 'Pregnancy Check', required: true, optionsKey: 'animals' },
    { name: 'test_date', label: 'Test Date', type: 'date', section: 'Pregnancy Check', required: true },
    { name: 'result', label: 'Result', type: 'select', section: 'Pregnancy Check', required: true, staticOptions: [{ value: 'Positive', label: 'Positive' }, { value: 'Negative', label: 'Negative' }] },
    { name: 'expected_birth_date', label: 'Expected Birth Date', type: 'date', section: 'Pregnancy Check', optional: true },
  ],
};

// One Male/Female pick per calf (calf_1_sex...) - UI-only fields, sent as
// calf_genders. Up to 4 calves get their own pick.
const MAX_CALF_PICKS = 4;
const calfSexKey = (i: number) => `calf_${i + 1}_sex`;
const isNewRecord = (v: Record<string, unknown>) => v.id === undefined;

function calvingPayload(v: Record<string, unknown>): Record<string, unknown> & { calf_count: number; calf_genders?: unknown[] } {
  const count = Number(v.calf_count) || 0;
  const genders = Array.from({ length: Math.min(count, MAX_CALF_PICKS) }, (_, i) => v[calfSexKey(i)]);
  return {
    ...v,
    calf_count: count,
    // Only when every calf has one - a record with more than 4 calves keeps its counts as they are.
    calf_genders: count <= MAX_CALF_PICKS && genders.every(Boolean) ? genders : undefined,
  };
}

function calvesText(r: CalvingRecord): string {
  const f = r.female_calves ?? 0;
  const m = r.male_calves ?? 0;
  if (f + m === 0) return `${r.calf_count} · sex not recorded`;
  if (r.calf_count === 1) return `1 · ${f ? 'Female' : 'Male'}`;
  return `${r.calf_count} · ${[f && `${f} Female`, m && `${m} Male`].filter(Boolean).join(' + ')}`;
}

const calvingConfig: ModuleConfig<CalvingRecord> = {
  title: 'Calving', entityLabel: 'calving record', module: 'Calving', queryKey: 'calving-records', idKey: 'id',
  api: {
    list: calvingRecordsApi.list,
    create: (v) => calvingRecordsApi.create(calvingPayload(v as Record<string, unknown>) as Partial<CalvingRecord>),
    update: (id, v) => {
      const p = calvingPayload(v as Record<string, unknown>);
      return calvingRecordsApi.update(id, {
        calving_type: p.calving_type || null, complications: p.complications || null,
        calf_count: p.calf_count, outcome: p.outcome, calf_genders: p.calf_genders,
      } as Partial<CalvingRecord>);
    },
  },
  referenceKeys: ['animals'],
  defaultValues: () => ({ calving_date: today(), calf_count: 1, outcome: 'Live', calf_1_sex: '' } as Partial<CalvingRecord>),
  // Each calf's pick, from the record's female/male counts (females first).
  deriveEditValues: (r) => Object.fromEntries(Array.from({ length: MAX_CALF_PICKS }, (_, i) => {
    const f = r.female_calves ?? 0;
    const m = r.male_calves ?? 0;
    return [calfSexKey(i), i < f ? 'Female' : i < f + m ? 'Male' : ''];
  })),
  columns: [
    { key: 'animal', header: 'Dam', primary: true, render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'date', header: 'Calving Date', render: (r) => formatDate(r.calving_date) },
    { key: 'type', header: 'Type', render: (r) => r.calving_type || '—' },
    { key: 'count', header: 'Calves', render: (r) => calvesText(r) },
    { key: 'outcome', header: 'Outcome', render: (r) => <ILMSStatusBadge status={r.outcome} size="sm" /> },
  ],
  fields: [
    // The dam and date can't be changed once saved - add a new record instead.
    { name: 'animal_id', label: 'Dam', type: 'select', section: 'Calving', required: isNewRecord, visibleWhen: isNewRecord, optionsKey: 'animals' },
    { name: 'calving_date', label: 'Calving Date', type: 'date', section: 'Calving', required: isNewRecord, visibleWhen: isNewRecord },
    { name: 'calving_type', label: 'Calving Type', type: 'select', section: 'Calving', optional: true, staticOptions: [
      { value: 'Normal', label: 'Normal' }, { value: 'Assisted', label: 'Assisted' }, { value: 'CSection', label: 'C-Section' },
    ] },
    { name: 'calf_count', label: 'Calves Born', type: 'number', section: 'Calving', required: true, min: 1 },
    { name: 'outcome', label: 'Outcome', type: 'select', section: 'Calving', required: true, staticOptions: [
      { value: 'Live', label: 'Live' }, { value: 'Stillborn', label: 'Stillborn' }, { value: 'Mixed', label: 'Mixed' },
    ] },
    ...Array.from({ length: MAX_CALF_PICKS }, (_, i) => ({
      name: calfSexKey(i) as keyof CalvingRecord & string,
      label: `Calf ${i + 1} — Male or Female`,
      type: 'select' as const, section: 'Calves',
      required: (v: Record<string, unknown>) => Number(v.calf_count) > i && Number(v.calf_count) <= MAX_CALF_PICKS,
      visibleWhen: (v: Record<string, unknown>) => Number(v.calf_count) > i && Number(v.calf_count) <= MAX_CALF_PICKS,
      staticOptions: [{ value: 'Female', label: 'Female' }, { value: 'Male', label: 'Male' }],
      helperText: i === 0 ? 'Each calf born alive is added to Dairy → Calf Records, where you can add its tag, colour and weight.' : undefined,
    })),
    { name: 'complications', label: 'Complications', type: 'textarea', section: 'Calves', optional: true, fullWidth: true },
  ],
};

const lambingConfig: ModuleConfig<LambingKiddingRecord> = {
  title: 'Lambing / Kidding', entityLabel: 'lambing/kidding record', module: 'Calving', queryKey: 'lambing-kidding-records', api: lambingKiddingRecordsApi, idKey: 'id',
  referenceKeys: ['animals'], defaultValues: { birth_date: today(), offspring_count: 1, outcome: 'Live' },
  columns: [
    { key: 'animal', header: 'Dam', primary: true, render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'date', header: 'Birth Date', render: (r) => formatDate(r.birth_date) },
    { key: 'type', header: 'Birth Type', render: (r) => r.birth_type || '—' },
    { key: 'count', header: 'Offspring', render: (r) => r.offspring_count },
    { key: 'outcome', header: 'Outcome', render: (r) => <ILMSStatusBadge status={r.outcome} size="sm" /> },
  ],
  fields: [
    { name: 'animal_id', label: 'Dam (Ewe/Doe)', type: 'select', section: 'Birth', required: true, optionsKey: 'animals' },
    { name: 'birth_date', label: 'Birth Date', type: 'date', section: 'Birth', required: true },
    { name: 'birth_type', label: 'Birth Type', type: 'select', section: 'Birth', optional: true, staticOptions: [
      { value: 'Single', label: 'Single' }, { value: 'Twin', label: 'Twin' }, { value: 'Triplet+', label: 'Triplet+' },
    ] },
    { name: 'offspring_count', label: 'Offspring Count', type: 'number', section: 'Birth', required: true, min: 1 },
    { name: 'outcome', label: 'Outcome', type: 'select', section: 'Birth', required: true, staticOptions: [
      { value: 'Live', label: 'Live' }, { value: 'Stillborn', label: 'Stillborn' }, { value: 'Mixed', label: 'Mixed' },
    ] },
    { name: 'complications', label: 'Complications', type: 'textarea', section: 'Birth', optional: true, fullWidth: true },
  ],
};

const semenMasterConfig: ModuleConfig<SemenMaster> = {
  title: 'Semen / Bull Catalogue', entityLabel: 'semen entry', module: 'Breeding', queryKey: 'semen-master', api: semenMasterApi, idKey: 'id',
  referenceKeys: ['breeds', 'suppliers'], defaultValues: { active: true },
  columns: [
    { key: 'code', header: 'Bull Code', primary: true, render: (r) => r.bull_code },
    { key: 'name', header: 'Bull Name', render: (r) => r.bull_name || '—' },
    { key: 'cost', header: 'Straw Cost', align: 'right', render: (r) => formatNumber(r.straw_cost, 2) },
    { key: 'active', header: 'Active', render: (r) => (r.active ? 'Yes' : 'No') },
  ],
  fields: [
    { name: 'bull_code', label: 'Bull Code', type: 'text', section: 'Details', required: true },
    { name: 'bull_name', label: 'Bull Name', type: 'text', section: 'Details', optional: true },
    { name: 'breed_id', label: 'Breed', type: 'select', section: 'Details', optional: true, optionsKey: 'breeds' },
    { name: 'supplier_id', label: 'Supplier', type: 'select', section: 'Details', optional: true, optionsKey: 'suppliers' },
    { name: 'straw_cost', label: 'Straw Cost (₹)', type: 'decimal', section: 'Details', optional: true, min: 0 },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Details' },
  ],
};

const semenInventoryConfig: ModuleConfig<SemenInventory> = {
  title: 'Semen Inventory', entityLabel: 'semen lot', module: 'Breeding', queryKey: 'semen-inventory', api: semenInventoryApi, idKey: 'id',
  referenceKeys: ['semenMaster', 'inventoryLocations'], defaultValues: { received_date: today() },
  columns: [
    { key: 'lot', header: 'Lot Number', primary: true, render: (r) => r.lot_number },
    { key: 'received', header: 'Received', align: 'right', render: (r) => r.straws_received },
    { key: 'available', header: 'Available', align: 'right', render: (r) => r.straws_available },
    { key: 'expiry', header: 'Expiry', render: (r) => formatDate(r.expiry_date) },
  ],
  fields: [
    { name: 'semen_id', label: 'Bull / Semen', type: 'select', section: 'Lot', required: true, optionsKey: 'semenMaster' },
    { name: 'lot_number', label: 'Lot Number', type: 'text', section: 'Lot', required: true },
    { name: 'straws_received', label: 'Straws Received', type: 'number', section: 'Lot', required: true, min: 0 },
    { name: 'straws_available', label: 'Straws Available', type: 'number', section: 'Lot', required: true, min: 0 },
    { name: 'received_date', label: 'Received Date', type: 'date', section: 'Lot', required: true },
    { name: 'expiry_date', label: 'Expiry Date', type: 'date', section: 'Lot', optional: true },
    { name: 'storage_location_id', label: 'Storage Location', type: 'select', section: 'Lot', optional: true, optionsKey: 'inventoryLocations' },
  ],
};

const kpiConfig: ModuleConfig<BreedingKPI> = {
  title: 'Breeding KPIs', entityLabel: 'KPI snapshot', module: 'Breeding', queryKey: 'breeding-kpis',
  api: { list: breedingKpisApi.list, create: async () => { throw new Error('read-only — use Recompute from the Animal profile'); }, update: async () => { throw new Error('read-only'); } },
  idKey: 'id', readOnly: true, exportDateKey: 'as_of_date',
  columns: [
    { key: 'animal', header: 'Animal', primary: true, render: (r) => <AnimalTag id={r.animal_id} /> },
    { key: 'as_of', header: 'As Of', render: (r) => formatDate(r.as_of_date) },
    { key: 'days_open', header: 'Days Open', render: (r) => r.days_open ?? '—' },
    { key: 'services', header: 'Services/Conception', render: (r) => formatNumber(r.services_per_conception, 1) },
    { key: 'conception_rate', header: 'Conception Rate %', render: (r) => formatNumber(r.conception_rate_percent, 1) },
    { key: 'repeat', header: 'Repeat Breeder', render: (r) => (r.is_repeat_breeder ? 'Yes' : 'No') },
  ],
  fields: [],
};

// What needs attention this week, from each animal's latest service - the
// dates come from its species' breeding days (Farm Setup → Animal Types).
const ALERT_GROUPS: { key: 'pd_checks_due' | 'repeat_heat_watch' | 'dry_off_due' | 'calvings_soon' | 'calvings_later'; title: string; hint: string }[] = [
  { key: 'pd_checks_due', title: 'PD Checks Due', hint: 'No PD result yet — due within 7 days or overdue' },
  { key: 'repeat_heat_watch', title: 'Repeat Heat Watch', hint: 'Watch for heat within 3 days of these dates' },
  { key: 'dry_off_due', title: 'Dry-off Due', hint: 'Pregnant — dry-off within 15 days or overdue' },
  { key: 'calvings_soon', title: 'Calving Soon', hint: 'Within 30 days (or overdue)' },
  { key: 'calvings_later', title: 'Calving Later', hint: 'In 31–60 days' },
];

function dueText(a: BreedingAlert): string {
  if (a.days === 0) return 'today';
  return a.days > 0 ? `in ${a.days} day${a.days === 1 ? '' : 's'}` : `overdue ${-a.days} day${a.days === -1 ? '' : 's'}`;
}

function BreedingAlertsTab() {
  const alertsQuery = useQuery({ queryKey: ['breeding-alerts'], queryFn: () => breedingRecordsApi.alerts() });
  if (alertsQuery.isLoading) return <ILMSCardSkeleton count={2} />;
  if (alertsQuery.isError) return <ILMSAlert tone="critical">{getApiErrorMessage(alertsQuery.error, 'Unable to load breeding alerts.')}</ILMSAlert>;
  const alerts = alertsQuery.data!;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
      {ALERT_GROUPS.map((g) => {
        const rows = alerts[g.key];
        return (
          <ILMSCard key={g.key} title={`${g.title} (${rows.length})`} subtitle={g.hint}>
            {rows.length === 0 ? (
              <p className="text-secondary text-ink-400">Nothing due.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-ink-300/10">
                {rows.map((a) => (
                  <li key={`${a.kind}-${a.breeding_record_id}`} className="flex items-center justify-between gap-3 py-2">
                    <span className="text-body text-ink-900">{a.animal_label}</span>
                    <span className={`text-helper-text tabular-nums ${a.days < 0 ? 'text-critical-700 font-medium' : 'text-ink-500'}`}>
                      {formatDate(a.due_date)} · {dueText(a)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </ILMSCard>
        );
      })}
    </div>
  );
}

export default function BreedingPage() {
  return (
    <TabbedPage
      title="Breeding"
      subtitle="Heat detection through service, pregnancy, calving/lambing and reproduction KPIs (WF-03)."
      tabs={[
        { id: 'alerts', label: 'Alerts', render: () => <BreedingAlertsTab /> },
        { id: 'heat', label: 'Heat', render: () => <ResourceModule config={heatConfig} /> },
        { id: 'service', label: 'Service', render: () => <ResourceModule config={serviceConfig} /> },
        { id: 'pregnancy', label: 'Pregnancy', render: () => <ResourceModule config={pregnancyConfig} /> },
        { id: 'calving', label: 'Calving', render: () => <ResourceModule config={calvingConfig} /> },
        { id: 'lambing', label: 'Lambing/Kidding', render: () => <ResourceModule config={lambingConfig} /> },
        { id: 'semen-master', label: 'Semen Catalogue', render: () => <ResourceModule config={semenMasterConfig} /> },
        { id: 'semen-inventory', label: 'Semen Inventory', render: () => <ResourceModule config={semenInventoryConfig} /> },
        { id: 'kpis', label: 'KPIs', render: () => <ResourceModule config={kpiConfig} /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Breeding', heatConfig, serviceConfig, pregnancyConfig, calvingConfig, lambingConfig, semenMasterConfig, semenInventoryConfig);
